import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { handleAnchorClick } from "@/lib/scroll";
import { INTRO_DELAY } from "@/lib/intro";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-[0.12em]">
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.95, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => (
    <section
        id="home"
        data-testid="hero-section"
        className="relative overflow-hidden bg-mist"
    >
        <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-frost/50"
        />
        <div
            aria-hidden="true"
            className="pointer-events-none absolute -left-32 bottom-0 h-[320px] w-[320px] rounded-full bg-ice/70"
        />

        <div className="mx-auto max-w-7xl px-4 pb-24 pt-20 sm:px-6 sm:pb-28 sm:pt-28 lg:px-8 lg:pb-36 lg:pt-36">
            <div className="relative max-w-3xl">
                <h1 className="text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                    <MaskedLine delay={0.15 + INTRO_DELAY}>Websites that</MaskedLine>
                    <MaskedLine delay={0.28 + INTRO_DELAY}>make an</MaskedLine>
                    <MaskedLine delay={0.41 + INTRO_DELAY}>
                        <span className="relative inline-block">
                            impression
                            <span
                                aria-hidden="true"
                                className="absolute inset-x-0 bottom-[0.08em] -z-10 h-[0.32em] rounded-sm bg-frost"
                            />
                        </span>
                        .
                    </MaskedLine>
                </h1>

                <motion.p
                    data-testid="hero-subheadline"
                    className="mt-6 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.55 + INTRO_DELAY, ease: EASE }}
                >
                    Modern websites designed and built for businesses that want
                    to stand out online.
                </motion.p>

                <motion.div
                    className="mt-9 flex flex-wrap items-center gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.68 + INTRO_DELAY, ease: EASE }}
                >
                    <a
                        href="#work"
                        data-testid="hero-cta-work"
                        onClick={(e) => handleAnchorClick(e, "#work")}
                        className="group inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-dark"
                    >
                        See Example Work
                        <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                    </a>
                    <a
                        href="#contact"
                        data-testid="hero-cta-contact"
                        onClick={(e) => handleAnchorClick(e, "#contact")}
                        className="group inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:border-brand hover:bg-ice"
                    >
                        Start a Project
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </a>
                </motion.div>
            </div>
        </div>
    </section>
);

export default Hero;
